import { createContext, useEffect, useState, useCallback, use } from 'react';
import type { ReactNode } from 'react';
import { LazyDataLoader } from '@/lib/utils/lazy-data-loader';
import { DBCollection } from '@/lib/utils/constants-shared';

interface HistoricalEventsByPassageDataContextType {
  historicalEventsByPassage: HistoricalEventsByPassage[];
  getHistoricalEventsByPassageByPassageId: (passageId: string) => HistoricalEventsByPassage | undefined;
  loadHistoricalEventsByPassageByPassageId: (passageId: string) => Promise<HistoricalEventsByPassage | undefined>;
}

export const HistoricalEventsByPassageDataContext = createContext<HistoricalEventsByPassageDataContextType | undefined>(undefined);

interface HistoricalEventsByPassageDataProviderProps {
  children: ReactNode;
  historicalEventsByPassage: HistoricalEventsByPassage[];
}

export function HistoricalEventsByPassageDataProvider({ children, historicalEventsByPassage: initialData }: HistoricalEventsByPassageDataProviderProps) {
  const [loader] = useState(() => new LazyDataLoader<HistoricalEventsByPassage>(DBCollection.historicalEventsByPassage, 'passageId'));
  const [cachedItems, setCachedItems] = useState<HistoricalEventsByPassage[]>([]);

  useEffect(() => {
    if (initialData && initialData.length > 0) {
      loader.preload(initialData);
      setCachedItems(loader.getAllCached());
    }
  }, [initialData, loader]);

  const getHistoricalEventsByPassageByPassageId = useCallback((passageId: string): HistoricalEventsByPassage | undefined => {
    return cachedItems.find(item => item.passageId === passageId);
  }, [cachedItems]);

  const loadHistoricalEventsByPassageByPassageId = useCallback(async (passageId: string): Promise<HistoricalEventsByPassage | undefined> => {
    const item = await loader.get(passageId);
    if (item) {
      setCachedItems(loader.getAllCached());
    }
    return item;
  }, [loader]);

  return (
    <HistoricalEventsByPassageDataContext.Provider
      value={{
        historicalEventsByPassage: cachedItems,
        getHistoricalEventsByPassageByPassageId,
        loadHistoricalEventsByPassageByPassageId
      }}
    >
      {children}
    </HistoricalEventsByPassageDataContext.Provider>
  );
}

export function useHistoricalEventsByPassageData(passageId?: string) {
  const context = use(HistoricalEventsByPassageDataContext);
  const [data, setData] = useState<HistoricalEventsByPassage | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!passageId || !context) return;

    const cached = context.getHistoricalEventsByPassageByPassageId(passageId);
    if (cached) {
      setData(cached);
      return;
    }

    setIsLoading(true);
    context.loadHistoricalEventsByPassageByPassageId(passageId)
      .then(loaded => {
        setData(loaded);
        setIsLoading(false);
      })
      .catch(err => {
        console.error(`Failed to load historical events by passage for ${passageId}:`, err);
        setIsLoading(false);
      });
  }, [passageId, context]);

  return { data, isLoading, context };
}

